import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import PropTypes from 'prop-types';

import { useViewTracking } from 'hooks';

// Components
import { Mui, MuiLab } from 'components';

// Local components
import BannerContent from './BannerContent';

const useStyles = Mui.makeStyles(() => ({
  alert: {
    paddingBottom: 12,
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    '& .MuiAlert-message': { width: '100%' },
  },
}));

function BannerItem({
  type,
  slug,
  banner,
  onInteraction,
  totalBanners,
  currentIndex,
  progress,
  onNext,
  onPrev,
}) {
  // Hooks
  const classes = useStyles();
  const history = useHistory();
  const elementRef = useViewTracking(banner?.hasViewed, () => onInteraction(banner?.uid, 'view'), {
    threshold: 0.8,
    delay: 2000,
  });

  // Actions
  const handleClick = useCallback(
    url => {
      if (!banner?.hasClicked) onInteraction(banner?.uid, 'click');
      const hasUrl = !!url && typeof url === 'string';
      if (hasUrl && url.startsWith('http')) window.open(url, '_blank', 'noopener,noreferrer');
      if (hasUrl && url.startsWith('/')) history.push(`/${type}/${slug}${url}`);
    },
    [banner?.uid, onInteraction, type, slug, history, banner?.hasClicked],
  );

  return (
    <MuiLab.Alert
      ref={elementRef}
      severity={banner?.severity || 'info'}
      variant="standard"
      icon={false}
      className={classes.alert}>
      <Mui.Box display="flex" alignItems="center" width="100%">
        {totalBanners > 1 && (
          <Mui.Box mr={2} display="flex" alignItems="center">
            <Mui.IconButton color="inherit" onClick={onPrev}>
              <Mui.Icon>chevron_left</Mui.Icon>
            </Mui.IconButton>
          </Mui.Box>
        )}
        <Mui.Box flex={1} minWidth={0}>
          {banner?.title && <MuiLab.AlertTitle>{banner?.title}</MuiLab.AlertTitle>}
          <BannerContent content={banner?.content} onLinkClick={handleClick} />
        </Mui.Box>
        <Mui.Box ml={2} display="flex" alignItems="center" gridGap={16}>
          {banner?.ctaText && banner?.ctaHref && (
            <Mui.Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={() => handleClick(banner?.ctaHref)}>
              {banner?.ctaText}
            </Mui.Button>
          )}
          {totalBanners > 1 && (
            <Mui.Typography
              variant="caption"
              color="inherit"
              style={{ opacity: 0.8, fontWeight: 500, whiteSpace: 'nowrap' }}>
              {currentIndex + 1} / {totalBanners}
            </Mui.Typography>
          )}
        </Mui.Box>
        {totalBanners > 1 && (
          <Mui.Box ml={2} display="flex" alignItems="center">
            <Mui.IconButton color="inherit" onClick={onNext}>
              <Mui.Icon>chevron_right</Mui.Icon>
            </Mui.IconButton>
          </Mui.Box>
        )}
      </Mui.Box>
      {
        // prettier-ignore
        totalBanners > 1 && (
          <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 3, zIndex: 2, pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: 0, left: 0,width: '100%',height: '100%',backgroundColor: 'currentColor',opacity: 0.15 }} />
            <div style={{ position: 'absolute', top: 0, left: 0, width: `${progress}%`, height: '100%', backgroundColor: 'currentColor', opacity: 0.30, transition: 'width 100ms linear' }} />
          </div>
        )
      }
    </MuiLab.Alert>
  );
}

BannerItem.propTypes = {
  type: PropTypes.string,
  slug: PropTypes.string,
  banner: PropTypes.object.isRequired,
  onInteraction: PropTypes.func.isRequired,
  totalBanners: PropTypes.number.isRequired,
  currentIndex: PropTypes.number.isRequired,
  progress: PropTypes.number.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
};

export default BannerItem;
